import { isNoShowHidden } from "utils/tenantOverrides";

import {
  DateField,
  Field,
  GmsField,
  SelectField,
  StaffMemberDropdown,
  TextInput,
} from "./fields";

// BOOKED is not a stored status: a booked appointment nobody has actioned has a null
// appointment_status. The backend has to read BOOKED as "status is null" (KIN-2157).
const APPOINTMENT_STATUS_OPTIONS = [
  { label: "BOOKED", value: "BOOKED" },
  { label: "CANCELLED", value: "CANCELLED" },
  { label: "COMPLETED", value: "COMPLETED" },
  { label: "NO_SHOW", value: "NO_SHOW" },
];

const ConsultationsFilters = ({ filters, onFilterChange }) => (
  <>
    <DateField
      label="Start Date"
      value={filters.consuiltationStartDate}
      onChange={(v) => onFilterChange("consuiltationStartDate", v)}
      placeholder="Start date"
    />
    <DateField
      label="End Date"
      value={filters.consultationEndDate}
      onChange={(v) => onFilterChange("consultationEndDate", v)}
      placeholder="End date"
    />
    <Field label="Patient Search">
      <TextInput
        value={filters.patientSearch}
        onChange={(v) => onFilterChange("patientSearch", v)}
        placeholder="Search patient name"
      />
    </Field>
    <StaffMemberDropdown
      label="Staff Member"
      value={filters.staffMemberId}
      onChange={(v) => onFilterChange("staffMemberId", v)}
    />
  </>
);

const ImmunisationsFilters = ({ filters, onFilterChange }) => (
  <>
    <DateField
      label="Start Date"
      value={filters.dateGivenStart}
      onChange={(v) => onFilterChange("dateGivenStart", v)}
      placeholder="Start date"
    />
    <DateField
      label="End Date"
      value={filters.dateGivenEnd}
      onChange={(v) => onFilterChange("dateGivenEnd", v)}
      placeholder="End date"
    />
    <SelectField
      label="Type"
      options={[
        { label: "Influenza", value: "Influenza" },
        { label: "Covid 19", value: "Covid 19" },
        { label: "Childhood Schedule", value: "Childhood Schedule" },
        { label: "Pneumococcol", value: "Pneumococcol" },
      ]}
      value={filters.immunisationType}
      onChange={(v) => onFilterChange("immunisationType", v)}
    />
    <Field label="Product">
      <TextInput
        value={filters.immunisationProduct}
        onChange={(v) => onFilterChange("immunisationProduct", v)}
        placeholder="Product"
      />
    </Field>
    <Field label="Batch No">
      <TextInput
        value={filters.immunisationBatchNo}
        onChange={(v) => onFilterChange("immunisationBatchNo", v)}
        placeholder="Batch number"
      />
    </Field>
    <GmsField
      value={filters.patientGmsStatus}
      onChange={(v) => onFilterChange("patientGmsStatus", v)}
    />
  </>
);

const PrescriptionsFilters = ({ filters, onFilterChange }) => (
  <>
    <Field label="Drug Name">
      <TextInput
        value={filters.drugName}
        onChange={(v) => onFilterChange("drugName", v)}
        placeholder="Drug name"
      />
    </Field>
    <Field label="Manufacturer">
      <TextInput
        value={filters.drugManufacturer}
        onChange={(v) => onFilterChange("drugManufacturer", v)}
        placeholder="Manufacturer"
      />
    </Field>
    <StaffMemberDropdown
      label="Prescribed By"
      value={filters.doctorId}
      onChange={(v) => onFilterChange("doctorId", v)}
    />
    <DateField
      label="Prescribed Start"
      value={filters.prescribedStartDate}
      onChange={(v) => onFilterChange("prescribedStartDate", v)}
      placeholder="Start date"
    />
    <DateField
      label="Prescribed End"
      value={filters.prescribedEndDate}
      onChange={(v) => onFilterChange("prescribedEndDate", v)}
      placeholder="End date"
    />
  </>
);

// KIN-2147: this report lists referrals. The type is the referral's own free-text name, so it is searched as text;
// the old ECG / ABPM options matched nothing a practice records.
const ReferralsFilters = ({ filters, onFilterChange }) => (
  <>
    <Field label="Referral Type">
      <TextInput
        value={filters.referralType}
        onChange={(v) => onFilterChange("referralType", v)}
        placeholder="e.g. Cardiology"
      />
    </Field>
    <Field label="Diagnosis Code">
      <TextInput
        value={filters.diagnosisCode}
        onChange={(v) => onFilterChange("diagnosisCode", v)}
        placeholder="Diagnosis code"
      />
    </Field>
    <StaffMemberDropdown
      label="Staff Member"
      value={filters.doctorId}
      onChange={(v) => onFilterChange("doctorId", v)}
    />
    <DateField
      label="Start Date"
      value={filters.referralStartDate}
      onChange={(v) => onFilterChange("referralStartDate", v)}
      placeholder="Start date"
    />
    <DateField
      label="End Date"
      value={filters.referralEndDate}
      onChange={(v) => onFilterChange("referralEndDate", v)}
      placeholder="End date"
    />
  </>
);

const LettersFilters = ({ filters, onFilterChange }) => (
  <>
    <StaffMemberDropdown
      label="Staff Member"
      value={filters.doctorId}
      onChange={(v) => onFilterChange("doctorId", v)}
    />
    <DateField
      label="Created Start"
      value={filters.letterSentStartDate}
      onChange={(v) => onFilterChange("letterSentStartDate", v)}
      placeholder="Start date"
    />
    <DateField
      label="Created End"
      value={filters.letterEndDate}
      onChange={(v) => onFilterChange("letterEndDate", v)}
      placeholder="End date"
    />
  </>
);

const AccountingFilters = ({ filters, onFilterChange }) => (
  <>
    <DateField
      label="Start Date"
      value={filters.consultationStartDate}
      onChange={(v) => onFilterChange("consultationStartDate", v)}
      placeholder="Start date"
    />
    <DateField
      label="End Date"
      value={filters.consultationEndDate}
      onChange={(v) => onFilterChange("consultationEndDate", v)}
      placeholder="End date"
    />
    <Field label="Service Name">
      <TextInput
        value={filters.serviceName}
        onChange={(v) => onFilterChange("serviceName", v)}
        placeholder="Service name"
      />
    </Field>
    <StaffMemberDropdown
      label="Staff Member"
      value={filters.doctorId}
      onChange={(v) => onFilterChange("doctorId", v)}
    />
  </>
);

const AppointmentsFilters = ({ filters, onFilterChange }) => (
  <>
    <DateField
      label="Start Date"
      value={filters.startDate}
      onChange={(v) => onFilterChange("startDate", v)}
      placeholder="Start date"
    />
    <DateField
      label="End Date"
      value={filters.endDate}
      onChange={(v) => onFilterChange("endDate", v)}
      placeholder="End date"
    />
    <Field label="Start Time">
      <TextInput
        value={filters.startTime}
        onChange={(v) => onFilterChange("startTime", v)}
        placeholder="HH:MM"
      />
    </Field>
    <Field label="End Time">
      <TextInput
        value={filters.endTime}
        onChange={(v) => onFilterChange("endTime", v)}
        placeholder="HH:MM"
      />
    </Field>
    <SelectField
      label="Consult Type"
      options={[
        { label: "Doctor Consultation", value: "Doctor Consultation" },
        { label: "Nurse Consultation", value: "Nurse Consultation" },
        { label: "Blood Test", value: "Blood Test" },
        { label: "Double Appointment", value: "Double Appointment" },
      ]}
      value={filters.consultType}
      onChange={(v) => onFilterChange("consultType", v)}
    />
    <SelectField
      label="Appointment Status"
      options={APPOINTMENT_STATUS_OPTIONS.filter(
        (option) => !(option.value === "NO_SHOW" && isNoShowHidden())
      )}
      value={filters.appointmentStatus}
      onChange={(v) => onFilterChange("appointmentStatus", v)}
    />
    <SelectField
      label="Money Owed"
      options={[
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ]}
      value={filters.moneyOwed}
      onChange={(v) => onFilterChange("moneyOwed", v)}
    />
    {/* KIN-2157: "Yes" asks for the hospital booking list, the appointment list plus each
        patient's address, healthcare details and the appointment summary. */}
    <SelectField
      label="Appointment Details (export)"
      options={[
        { label: "Yes", value: "true" },
        { label: "No", value: "false" },
      ]}
      value={filters.details}
      onChange={(v) => onFilterChange("details", v)}
    />
  </>
);

const MedicoLegalFilters = ({ filters, onFilterChange }) => (
  <>
    <DateField
      label="Start Date"
      value={filters.startDate}
      onChange={(v) => onFilterChange("startDate", v)}
      placeholder="Start date"
    />
    <DateField
      label="End Date"
      value={filters.endDate}
      onChange={(v) => onFilterChange("endDate", v)}
      placeholder="End date"
    />
    <Field label="Doctor ID">
      <TextInput
        value={filters.doctorId}
        onChange={(v) => onFilterChange("doctorId", v)}
        placeholder="Doctor ID"
      />
    </Field>
    <Field label="First Name">
      <TextInput
        value={filters.patientFirstname}
        onChange={(v) => onFilterChange("patientFirstname", v)}
        placeholder="First name"
      />
    </Field>
    <DateField
      label="DOB"
      value={filters.dateOfBirth}
      onChange={(v) => onFilterChange("dateOfBirth", v)}
      placeholder="Date of birth"
    />
  </>
);

const AuditLogFilters = ({ filters, onFilterChange }) => (
  <>
    <DateField
      label="Start Date"
      value={filters.startDate}
      onChange={(v) => onFilterChange("startDate", v)}
      placeholder="Start date"
    />
    <DateField
      label="End Date"
      value={filters.endDate}
      onChange={(v) => onFilterChange("endDate", v)}
      placeholder="End date"
    />
    <StaffMemberDropdown
      label="Staff Member"
      value={filters.staffId}
      onChange={(v) => onFilterChange("staffId", v)}
    />
  </>
);

// Last name has its own built-in "Search by last name" box in ReportsTable's toolbar (wired to the
// same patientLastname filter key) for every report type except appointments/CDM — only first name
// needs its own field here, matching MedicoLegalFilters' pattern.
const PatientMergesFilters = ({ filters, onFilterChange }) => (
  <>
    <Field label="First Name">
      <TextInput
        value={filters.patientFirstname}
        onChange={(v) => onFilterChange("patientFirstname", v)}
        placeholder="First name"
      />
    </Field>
    <DateField
      label="Merged From"
      value={filters.mergedDateFrom}
      onChange={(v) => onFilterChange("mergedDateFrom", v)}
      placeholder="Start date"
    />
    <DateField
      label="Merged To"
      value={filters.mergedDateTo}
      onChange={(v) => onFilterChange("mergedDateTo", v)}
      placeholder="End date"
    />
  </>
);

// Non-CDM report filter fields; rendered inside the shared filters grid.
export const StandardFilters = ({ reportType, filters, onFilterChange }) => {
  switch (reportType) {
    case "immunisations":
      return (
        <ImmunisationsFilters filters={filters} onFilterChange={onFilterChange} />
      );
    case "prescriptions":
      return (
        <PrescriptionsFilters filters={filters} onFilterChange={onFilterChange} />
      );
    case "investigations":
      return (
        <ReferralsFilters
          filters={filters}
          onFilterChange={onFilterChange}
        />
      );
    case "letters":
      return <LettersFilters filters={filters} onFilterChange={onFilterChange} />;
    case "accounting":
      return (
        <AccountingFilters filters={filters} onFilterChange={onFilterChange} />
      );
    case "appointments":
      return (
        <AppointmentsFilters filters={filters} onFilterChange={onFilterChange} />
      );
    case "medico-legal":
      return (
        <MedicoLegalFilters filters={filters} onFilterChange={onFilterChange} />
      );
    case "audit-log":
      return (
        <AuditLogFilters filters={filters} onFilterChange={onFilterChange} />
      );
    case "patient-merges":
      return (
        <PatientMergesFilters filters={filters} onFilterChange={onFilterChange} />
      );
    default:
      return (
        <ConsultationsFilters filters={filters} onFilterChange={onFilterChange} />
      );
  }
};
